import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import * as Switch from '@radix-ui/react-switch';
import { CaretDown, Check, Sliders, Sparkle } from '@phosphor-icons/react';
import PromptComposer from '../components/composer/PromptComposer';
import MediaModelPicker from '../components/composer/MediaModelPicker';
import { COMPOSER_ASPECT_RATIO_OPTIONS } from '../lib/aspectRatioOptions';
import { LANDING_EVENTS, trackLandingEvent } from './landingAnalytics';

export const ASPECT_RATIO_OPTIONS = COMPOSER_ASPECT_RATIO_OPTIONS;

export function AspectRatioIcon({ w, h, size = 16 }) {
  const maxDim = size * 0.75;
  const scale = maxDim / Math.max(w, h);
  const rw = w * scale;
  const rh = h * scale;
  const x = (size - rw) / 2;
  const y = (size - rh) / 2;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden="true">
      <rect
        x={x} y={y} width={rw} height={rh}
        rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.2"
      />
    </svg>
  );
}

function SuggestionChips({ suggestions, promptText, onApply, disabled }) {
  const [pendingSuggestion, setPendingSuggestion] = useState(null);

  const applySuggestion = (suggestion) => {
    onApply(suggestion);
    setPendingSuggestion(null);
  };

  const handleSelect = (suggestion) => {
    if (promptText.trim().length > 0) {
      setPendingSuggestion(suggestion);
      return;
    }
    applySuggestion(suggestion);
  };

  return (
    <div className="lp-suggestions">
      {pendingSuggestion && (
        <div className="lp-suggestions__confirm" role="alertdialog" aria-label="Replace current prompt">
          <span>Replace your current prompt with “{pendingSuggestion.label}”?</span>
          <span className="lp-suggestions__confirm-actions">
            <button
              type="button"
              className="lp-btn-quiet lp-focusable"
              onClick={() => setPendingSuggestion(null)}
            >
              Keep mine
            </button>
            <button
              type="button"
              className="lp-btn-quiet lp-btn-quiet--solid lp-focusable"
              onClick={() => applySuggestion(pendingSuggestion)}
            >
              Replace
            </button>
          </span>
        </div>
      )}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button type="button" className="lp-suggestions__trigger lp-focusable" disabled={disabled}>
            <Sparkle size={14} weight="bold" aria-hidden="true" />
            Try an example
            <CaretDown size={11} weight="bold" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="lp-suggestions__menu" side="bottom" sideOffset={6} align="center" collisionPadding={12}>
            <DropdownMenu.Label>Start with an example</DropdownMenu.Label>
            {suggestions.map((suggestion) => (
              <DropdownMenu.Item
                key={suggestion.id}
                className="lp-suggestions__item"
                onSelect={() => handleSelect(suggestion)}
              >
                {suggestion.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

function RunSettings({
  aspectRatio,
  onAspectRatioChange,
  multiAgent,
  onMultiAgentChange,
  extraCareful,
  onExtraCarefulChange,
  mediaModelSelection,
  onMediaModelSelectionChange,
  llmModel,
  onLlmModelChange,
  authenticated,
  isSubmitting,
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const selectedRatio = ASPECT_RATIO_OPTIONS.find((option) => option.value === aspectRatio)
    || ASPECT_RATIO_OPTIONS[0];
  const summary = [
    selectedRatio.label || selectedRatio.value,
    multiAgent ? 'Multi-Agent' : 'Single',
    extraCareful ? 'Extra careful' : 'Standard',
  ].join(' · ');

  return <Popover.Root open={settingsOpen} onOpenChange={(open) => {
    setSettingsOpen(open);
    if (!open) setAdvancedOpen(false);
  }}>
    <Popover.Trigger asChild>
      <button
        type="button"
        className="lp-tool-btn lp-run-settings__trigger lp-focusable"
        aria-label={`Run settings: ${summary}`}
        disabled={isSubmitting}
      >
        <Sliders size={15} weight="bold" aria-hidden="true" />
        <span>{summary}</span>
        <CaretDown size={10} weight="bold" aria-hidden="true" />
      </button>
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Content className="lp-run-settings" side="bottom" sideOffset={7} align="start" collisionPadding={12}>
        {advancedOpen ? <div className="lp-run-settings__advanced-view">
          <button type="button" className="lp-run-settings__back" onClick={() => setAdvancedOpen(false)}>
            <CaretDown size={12} weight="bold" aria-hidden="true" />
            Back to run settings
          </button>
          <fieldset className="lp-run-settings__model-panel" disabled={isSubmitting}>
            <MediaModelPicker
              embedded
              value={mediaModelSelection}
              onChange={onMediaModelSelectionChange}
              llmModel={llmModel}
              onLlmModelChange={onLlmModelChange}
              onEvent={trackLandingEvent}
            />
          </fieldset>
        </div> : <>
          <section className="lp-run-settings__section" aria-labelledby="lp-output-format-label">
            <div id="lp-output-format-label" className="lp-run-settings__section-label">Output format</div>
            <div className="lp-run-settings__ratios">
              {ASPECT_RATIO_OPTIONS.map(({ value, label, description, w, h }) => (
                <button
                  key={value}
                  type="button"
                  className={aspectRatio === value ? 'is-selected' : ''}
                  aria-pressed={aspectRatio === value}
                  onClick={() => onAspectRatioChange(value)}
                  disabled={isSubmitting}
                >
                  <span className="lp-run-settings__ratio-icon"><AspectRatioIcon w={w} h={h} size={18} /></span>
                  <span><strong>{label || value}</strong><small>{description}</small></span>
                  {aspectRatio === value && <Check size={13} weight="bold" aria-hidden="true" />}
                </button>
              ))}
            </div>
          </section>

          <div className="lp-run-settings__section lp-run-settings__choices">
            <label>
              <span><strong>Multi-Agent</strong><small>Plan independent video directions in parallel.</small></span>
              <Switch.Root className="lp-run-settings__switch" checked={multiAgent} onCheckedChange={onMultiAgentChange} disabled={isSubmitting} aria-label="Multi-Agent">
                <Switch.Thumb className="lp-run-settings__switch-thumb" />
              </Switch.Root>
            </label>
            <label>
              <span><strong>Extra careful</strong><small>Inspect generated video and retry visible mistakes.</small></span>
              <Switch.Root className="lp-run-settings__switch" checked={extraCareful} onCheckedChange={onExtraCarefulChange} disabled={isSubmitting} aria-label="Extra careful">
                <Switch.Thumb className="lp-run-settings__switch-thumb" />
              </Switch.Root>
            </label>
          </div>

          {authenticated && <div className="lp-run-settings__advanced">
            <button type="button" className="lp-run-settings__advanced-trigger" onClick={() => setAdvancedOpen(true)} aria-expanded={false} disabled={isSubmitting}>
              <span><strong>Advanced models</strong><small>{mediaModelSelection ? 'Custom routing selected' : 'Composer chooses automatically'}</small></span>
              <CaretDown size={12} weight="bold" aria-hidden="true" />
            </button>
          </div>}
        </>}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>;
}

/**
 * Shared, controlled Composer surface. The hero, final CTA, and
 * authenticated home all render this with the same owned state, so there
 * is never a second independent prompt value.
 */
function LandingComposer({
  location,
  placeholder,
  promptText,
  onPromptChange,
  onKeyDown,
  onSubmit,
  textareaRef,
  picker,
  references,
  onRemoveReference,
  attachments,
  onRemoveAttachment,
  attachmentsLoading,
  aspectRatio,
  onAspectRatioChange,
  multiAgent,
  onMultiAgentChange,
  extraCareful,
  onExtraCarefulChange,
  mediaModelSelection,
  onMediaModelSelectionChange,
  llmModel,
  onLlmModelChange,
  authenticated,
  fileInputRef,
  onFileInput,
  onAttachClick,
  isSubmitting,
  submitFeedback,
  isDragging,
  canSubmit,
  errorMessage,
  hintMessage,
  formatFileSize: _formatFileSize,
  suggestions,
  onApplySuggestion,
  riskReversal,
  onComposerFocus,
  dropHint = 'Drop files to add them.',
}) {
  return (
    <div>
      <PromptComposer
        name="Video prompt composer"
        location={location}
        placeholder={placeholder}
        promptText={promptText}
        onPromptChange={onPromptChange}
        onKeyDown={onKeyDown}
        onSubmit={onSubmit}
        textareaRef={textareaRef}
        picker={picker}
        references={references}
        onRemoveReference={onRemoveReference}
        attachments={attachments}
        onRemoveAttachment={onRemoveAttachment}
        attachmentsLoading={attachmentsLoading}
        fileInputRef={fileInputRef}
        onFileInput={onFileInput}
        onAttachClick={onAttachClick}
        isSubmitting={isSubmitting}
        submitFeedback={submitFeedback}
        isDragging={isDragging}
        canSubmit={canSubmit}
        errorMessage={errorMessage}
        hintMessage={hintMessage}
        onComposerFocus={() => onComposerFocus?.(location)}
        submitLabel="Create my video"
        submittingLabel={multiAgent ? 'Planning Multi-Agent projects' : 'Creating video'}
        dropHint={dropHint}
        toolbarControls={<>
          <RunSettings
            aspectRatio={aspectRatio}
            onAspectRatioChange={onAspectRatioChange}
            multiAgent={multiAgent}
            onMultiAgentChange={onMultiAgentChange}
            extraCareful={extraCareful}
            onExtraCarefulChange={onExtraCarefulChange}
            mediaModelSelection={mediaModelSelection}
            onMediaModelSelectionChange={onMediaModelSelectionChange}
            llmModel={llmModel}
            onLlmModelChange={onLlmModelChange}
            authenticated={authenticated}
            isSubmitting={isSubmitting}
          />
        </>}
      />

      {suggestions?.length > 0 && (
        <SuggestionChips
          suggestions={suggestions}
          promptText={promptText}
          disabled={isSubmitting}
          onApply={(suggestion) => {
            trackLandingEvent(LANDING_EVENTS.suggestionSelected, { suggestion_id: suggestion.id });
            onApplySuggestion(suggestion, location);
          }}
        />
      )}

      {riskReversal && <p className="lp-risk-reversal">{riskReversal}</p>}
    </div>
  );
}

export default LandingComposer;
